import { useTheme } from '@emotion/react';
import { match } from 'ts-pattern';

type BarChartLayoutProps = {
  referenceLineValue: number;
  invertColors: boolean;
  variant: 'simple' | 'default';
  type?: 'survivor-prop' | 'others';
  /** Narrow layout: fewer visual accents on `simple` charts (opt-in; default unchanged). */
  compactSimple?: boolean;
};

const useBarChartLayout = ({
  referenceLineValue,
  invertColors,
  variant,
  type,
  compactSimple,
}: BarChartLayoutProps) => {
  const theme = useTheme();

  const getLabelColor = (value: number) => {
    const greenBar = { barColor: theme.colors.green[2], borderColor: theme.colors.green[3] };
    const grayBar = { barColor: theme.colors.gray2[2], borderColor: theme.colors.gray2[4] };

    if (type === 'survivor-prop' && referenceLineValue && value >= referenceLineValue) {
      return invertColors ? greenBar : grayBar;
    }

    if (referenceLineValue && value > referenceLineValue) {
      return invertColors ? greenBar : grayBar;
    }

    return invertColors ? grayBar : greenBar;
  };

  const layoutConfig = match({ variant, compactSimple })
    .with({ variant: 'simple', compactSimple: true }, () => ({
      barChartMargin: { top: 2, bottom: 2, left: 0, right: 0 },
      showHorizontalGrid: false,
      showXAxis: false,
      showYAxis: false,
      showLabelList: false,
      yAxisPadding: { top: 0, bottom: 0 },
      isAnimationActive: false,
      barSize: 5,
      barTopHeight: 1,
      hideSimpleBarCap: true,
    }))
    .with({ variant: 'simple' }, () => ({
      barChartMargin: undefined,
      showHorizontalGrid: false,
      showXAxis: false,
      showYAxis: false,
      showLabelList: false,
      yAxisPadding: { top: 0, bottom: 0 },
      isAnimationActive: false,
      barSize: 4,
      barTopHeight: 1,
      hideSimpleBarCap: false,
    }))
    .with({ variant: 'default' }, () => ({
      barChartMargin: {
        top: 5,
        left: -10,
        right: 20,
        bottom: 5,
      },
      showHorizontalGrid: true,
      showXAxis: true,
      showYAxis: true,
      showLabelList: true,
      yAxisPadding: { top: 16, bottom: 1 },
      isAnimationActive: true,
      barSize: 24,
      barTopHeight: 2,
      hideSimpleBarCap: false,
    }))
    .exhaustive();

  return {
    getLabelColor,
    layoutConfig,
  };
};

export default useBarChartLayout;
